import { useState } from "react";
import { Modal, Empty } from "antd";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { EyeOutlined } from "@ant-design/icons";
import { useCompetenciesByIds } from "../../api/CompetenciesAPI";
import { agGridThemeVars, agGridDefaultColDef } from "../../constants/agGridTheme";

const PHASE_ORDER = ["empathize", "define", "ideate", "prototype", "test", "communicate"];

const CATEGORY_COLORS = {
    skills: "#4CAF50",
    knowledge: "#2196F3",
    mindset: "#FF9800",
    instruments: "#9C27B0",
};

// Flattens competencies into rows sorted by design-thinking phase order, then ability, then name.
const toSortedRows = (competencies) =>
    [...competencies]
        .sort((a, b) => {
            const phaseA = (a.abilities?.phase || "").toLowerCase();
            const phaseB = (b.abilities?.phase || "").toLowerCase();
            const phaseIndexA = PHASE_ORDER.indexOf(phaseA);
            const phaseIndexB = PHASE_ORDER.indexOf(phaseB);
            const phaseDiff =
                (phaseIndexA === -1 ? PHASE_ORDER.length : phaseIndexA) -
                (phaseIndexB === -1 ? PHASE_ORDER.length : phaseIndexB);
            if (phaseDiff !== 0) return phaseDiff;

            const abilityDiff = (a.abilities?.name || "").localeCompare(b.abilities?.name || "");
            if (abilityDiff !== 0) return abilityDiff;

            return a.name.localeCompare(b.name);
        })
        .map((competency) => ({ key: competency.id, ...competency }));

const categoryRenderer = (params) => {
    const category = params.value;
    return (
        <span
            className="text-[10px] font-medium px-2 py-0.5 rounded capitalize text-white"
            style={{ backgroundColor: CATEGORY_COLORS[category] || "#999999" }}
        >
            {category || "uncategorized"}
        </span>
    );
};

const columnDefs = [
    {
        headerName: "Competency",
        field: "name",
        minWidth: 260,
        flex: 2,
    },
    {
        headerName: "Ability",
        minWidth: 160,
        flex: 1,
        valueGetter: (params) => params.data?.abilities?.name || "Unassigned ability",
    },
    {
        headerName: "Phase",
        width: 90,
        valueGetter: (params) => params.data?.abilities?.phase || "Unassigned",
        cellRenderer: (params) => <span className="capitalize">{params.value}</span>,
    },
    {
        headerName: "Category",
        field: "category",
        width: 90,
        cellRenderer: categoryRenderer,
    },
];

const Summary = ({ toggledCompetencies = [] }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { data: competencies = [], isLoading } = useCompetenciesByIds(toggledCompetencies);
    const rows = toSortedRows(competencies);

    return (
        <>
            <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="rounded-lg bg-white p-4 absolute top-0 left-0 my-4 mx-8 z-50 shadow-md text-left hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
                <p className="text-xs">Competencies selected: {toggledCompetencies.length}</p>
                <EyeOutlined className="text-xs text-gray-400" />
            </button>

            <Modal
                title="Selected competencies"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={640}
            >
                {toggledCompetencies.length === 0 ? (
                    <Empty description="No competencies selected yet" />
                ) : (
                    <div
                        className="ag-theme-alpine"
                        style={{
                            height: 400,
                            width: "100%",
                            ...agGridThemeVars,
                        }}
                    >
                        <AgGridReact
                            rowData={rows}
                            columnDefs={columnDefs}
                            defaultColDef={agGridDefaultColDef}
                            loading={isLoading}
                            domLayout="normal"
                        />
                    </div>
                )}
            </Modal>
        </>
    );
};

export default Summary;